/**
 * The languages the game speaks (L10N1; Spanish since L10N2). English is the source every other
 * table is checked against; a language is added by its code here, its label and a column in every
 * table of `text/` (the steps: `src/i18n/README.md`, "Adding a language").
 */
export const LANGUAGES = ["en", "ru", "uk", "es"] as const;
export type Language = (typeof LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = "en";

/**
 * How the switcher names a language: in its own words (the tooltip and the screen reader), and
 * the two letters on its pennant. Ukrainian's code is `uk`, but "UK" reads as a country.
 */
export const LANGUAGE_LABELS: Readonly<
  Record<Language, { readonly name: string; readonly mark: string }>
> = {
  en: { name: "English", mark: "EN" },
  ru: { name: "Русский", mark: "RU" },
  uk: { name: "Українська", mark: "UA" },
  es: { name: "Español", mark: "ES" },
};

/** One namespace's words in every language; each column must have the English column's shape. */
export type Translations<Shape> = Readonly<Record<Language, Shape>>;

export function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * The first of a browser's preferred languages (`navigator.languages`, "uk-UA", "ru") that the
 * game speaks, by its primary subtag; English when none is.
 */
export function languageOf(preferred: readonly string[]): Language {
  for (const tag of preferred) {
    const primary = tag.toLowerCase().split(/[-_]/)[0];
    if (isLanguage(primary)) {
      return primary;
    }
  }
  return DEFAULT_LANGUAGE;
}
